/**
 * Journal Service - Supabase Integration
 * Handles all journal entry CRUD operations
 */

import { supabase } from './supabaseClient';

/**
 * Get all journal entries for a member
 */
export const getJournalEntries = async (memberEmail) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('member_id', member.id)
      .order('entry_date', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching journal entries:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get a single journal entry by date
 */
export const getJournalEntryByDate = async (memberEmail, date) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: false, error: 'Member not found' };

    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('member_id', member.id)
      .eq('entry_date', date)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return { success: true, data: data || null };
  } catch (error) {
    console.error('Error fetching journal entry:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create or update a journal entry
 */
export const saveJournalEntry = async (memberEmail, entryData) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) throw new Error('Member not found');

    // Check if entry exists for this date
    const { data: existing } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('member_id', member.id)
      .eq('entry_date', entryData.entry_date)
      .single();

    let result;
    if (existing) {
      // Update existing entry
      result = await supabase
        .from('journal_entries')
        .update(entryData)
        .eq('id', existing.id)
        .select('*')
        .single();
    } else {
      // Create new entry
      result = await supabase
        .from('journal_entries')
        .insert([{
          member_id: member.id,
          ...entryData
        }])
        .select('*')
        .single();
    }

    if (result.error) throw result.error;
    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error saving journal entry:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a journal entry
 */
export const deleteJournalEntry = async (memberEmail, entryDate) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) throw new Error('Member not found');

    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('member_id', member.id)
      .eq('entry_date', entryDate);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting journal entry:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get journal entries for a date range
 */
export const getJournalEntriesInRange = async (memberEmail, startDate, endDate) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('member_id', member.id)
      .gte('entry_date', startDate)
      .lte('entry_date', endDate)
      .order('entry_date', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching journal entries in range:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get journal statistics
 */
export const getJournalStats = async (memberEmail) => {
  try {
    const result = await getJournalEntries(memberEmail);
    if (!result.success) return { success: false, error: result.error };

    const entries = result.data;
    const stats = {
      totalEntries: entries.length,
      currentStreak: 0,
      longestStreak: 0,
      averageMood: 0,
      averageEnergy: 0,
      averageSleep: 0
    };

    if (entries.length === 0) return { success: true, data: stats };

    // Calculate averages
    let moodSum = 0, moodCount = 0;
    let energySum = 0, energyCount = 0;
    let sleepSum = 0, sleepCount = 0;

    entries.forEach(entry => {
      if (entry.mood) {
        const moodValue = { 'great': 5, 'good': 4, 'okay': 3, 'struggling': 2 }[entry.mood] || 3;
        moodSum += moodValue;
        moodCount++;
      }
      if (entry.energy_level) {
        energySum += entry.energy_level;
        energyCount++;
      }
      if (entry.sleep_quality) {
        sleepSum += entry.sleep_quality;
        sleepCount++;
      }
    });

    stats.averageMood = moodCount > 0 ? (moodSum / moodCount).toFixed(1) : 0;
    stats.averageEnergy = energyCount > 0 ? (energySum / energyCount).toFixed(1) : 0;
    stats.averageSleep = sleepCount > 0 ? (sleepSum / sleepCount).toFixed(1) : 0;

    // Calculate streaks
    const sortedDates = entries
      .map(e => new Date(e.entry_date))
      .sort((a, b) => b - a);

    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 1;

    for (let i = 0; i < sortedDates.length; i++) {
      if (i === 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const entryDate = new Date(sortedDates[i]);
        entryDate.setHours(0, 0, 0, 0);
        
        const diffDays = Math.floor((today - entryDate) / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) {
          currentStreak = 1;
        }
      }

      if (i > 0) {
        const prevDate = new Date(sortedDates[i - 1]);
        const currDate = new Date(sortedDates[i]);
        const diffDays = Math.floor((prevDate - currDate) / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
          tempStreak++;
          if (i === 1 && currentStreak > 0) currentStreak++;
        } else {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
        }
      }
    }

    longestStreak = Math.max(longestStreak, tempStreak);
    stats.currentStreak = currentStreak;
    stats.longestStreak = longestStreak;

    return { success: true, data: stats };
  } catch (error) {
    console.error('Error calculating journal stats:', error);
    return { success: false, error: error.message };
  }
};

export default {
  getJournalEntries,
  getJournalEntryByDate,
  saveJournalEntry,
  deleteJournalEntry,
  getJournalEntriesInRange,
  getJournalStats
};
